import type { CameDuringPauseItem } from "@teamna/shared";
import { formatDayKey } from "@teamna/shared";
import { useAnswerCameDuringPause, useCameDuringPause } from "@teamna/web-client/hooks/use-coach-participants.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { formatDateTime } from "@teamna/web-client/lib/format.ts";
import { messageForError } from "../components/CodeFlow.tsx";
import { Avatar, Button, ErrorState, SkeletonList, showToast } from "../components/index.ts";
import { StaleNotice } from "./todo/stale-notice.tsx";

/**
 * "Came during the pause", a row of the owner's To do list on Home (Asana "Attendance: sessions left
 * on the register", signed off 2026-10-29). Came on the register took 1 session from a package that
 * is paused, and the pause stayed. The owner ends the pause today with one tap, or keeps it.
 */
export function CameDuringPauseList({ businessId, timezone }: { businessId: string; timezone: string }) {
  const { label } = useLabels();
  const list = useCameDuringPause(businessId);
  const items = list.data?.items ?? [];
  if (!list.data) return list.isLoading ? <SkeletonList rows={2} /> : <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  const stale = list.isError ? <StaleNotice onRetry={() => void list.refetch()} /> : null;
  if (items.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {stale}
        <p className="text-[13.5px] text-ink-2">Nothing to answer.</p>
      </div>
    );
  }
  return (
    <section aria-label="Came during the pause" className="flex flex-col gap-2">
      {stale}
      <p className="text-[13.5px] text-ink-2">
        These {label("participant", { plural: true })} came while their package is paused. Came took 1 session, and the pause stays until you end it.
      </p>
      {items.map((item) => (
        <PauseRow key={item.id} businessId={businessId} item={item} timezone={timezone} />
      ))}
    </section>
  );
}

function PauseRow({ businessId, item, timezone }: { businessId: string; item: CameDuringPauseItem; timezone: string }) {
  const answer = useAnswerCameDuringPause(businessId);
  const first = item.name.split(" ")[0] ?? item.name;
  const run = (end: boolean) =>
    answer.mutate(
      { id: item.id, end },
      {
        onSuccess: () => showToast(end ? `The pause of ${first}'s package ended today.` : `The pause of ${first}'s package stays.`),
        onError: (error) => showToast(messageForError(error), { tone: "info" }),
      },
    );
  return (
    <article className="flex flex-col gap-2 rounded-card border border-line bg-paper px-4 py-3" aria-label={item.name}>
      <div className="flex items-start gap-3">
        <Avatar name={item.name} photoUrl={null} size={32} />
        <div className="min-w-0 flex-1 text-[14px]">
          <p className="text-ink">
            <b dir="auto">{item.name}</b> came to {item.groupName}, {formatDateTime(item.startsAt, timezone)}.
          </p>
          <p className="text-[12.5px] text-ink-2">The package is paused until {formatDayKey(item.pausedUntil)}. End the pause now?</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" loading={answer.isPending} onClick={() => run(true)}>
          End the pause
        </Button>
        <Button size="sm" variant="secondary" loading={answer.isPending} onClick={() => run(false)}>
          Keep the pause
        </Button>
      </div>
    </article>
  );
}
